import React, { useState } from 'react';
import { DollarSign, Loader } from 'lucide-react';
import { aiAPI } from '../services/api';
import toast from 'react-hot-toast';
import MarkdownRenderer from './MarkdownRenderer';
import StatsAndTabs from './StatsAndTabs';

const CostEstimate = ({ onSuccess, stats, loading: statsLoading }) => {
  const [formData, setFormData] = useState({
    projectDescription: '',
    scope: ''
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.projectDescription.trim() || !formData.scope.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    setResult('');

    try {
      const response = await aiAPI.estimateCost({
        projectDescription: formData.projectDescription,
        scope: formData.scope
      });

      setResult(response.result);
      toast.success('Cost estimate completed!');
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Cost estimation failed:', error);
      toast.error('Failed to generate cost estimate');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFormData({
      projectDescription: '',
      scope: ''
    });
    setResult('');
  };

  return (
    <div>
      <div className="content-header">
        <div className="welcome-section">
          <h2>Cost Estimate</h2>
          <p>Get AI-powered cost estimates for legal projects</p>
        </div>
      </div>

      <StatsAndTabs stats={stats} loading={statsLoading} />

      <div className="tool-content">
        <div className="tool-header">
          <DollarSign size={24} />
          <h3>Legal Cost Estimator</h3>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Project Description</label>
            <input
              type="text"
              name="projectDescription"
              className="form-input"
              placeholder="Brief description of the legal project..."
              value={formData.projectDescription}
              onChange={handleChange}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Project Scope & Details</label>
            <textarea
              name="scope"
              className="form-textarea"
              placeholder="Detailed scope, complexity, timeline, and requirements..."
              value={formData.scope}
              onChange={handleChange}
              disabled={loading}
              style={{ minHeight: '150px' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader size={16} className="animate-spin" />
                  Calculating Estimate...
                </>
              ) : (
                <>
                  <DollarSign size={16} />
                  Generate Estimate
                </>
              )}
            </button>

            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClear}
              disabled={loading}
            >
              Clear
            </button>
          </div>
        </form>
      </div>

      {result && (
        <div className="tool-content">
          <div className="tool-header">
            <DollarSign size={24} />
            <h3>Cost Estimate</h3>
          </div>
          
          <MarkdownRenderer content={result} />

          <div style={{ marginTop: '16px' }}>
            <button
              className="btn btn-secondary"
              onClick={() => navigator.clipboard.writeText(result)}
            >
              Copy Estimate
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CostEstimate;
